import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2, User, Bot, Reply, Paperclip, Download, Mail, Sparkles, Power } from 'lucide-react';
import { Conversation, Message } from '../../types/conversation';
import { flooringBotDb } from '../../lib/supabase';

export default function MessageThread({ conversation }: { conversation: Conversation }) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [autoResponseEnabled, setAutoResponseEnabled] = useState(false);
  const [generating, setGenerating] = useState(false);

  const isEmailConversation = conversation.channel === 'gmail';

  // Load messages when conversation changes
  useEffect(() => {
    if (!conversation?.id) return;

    loadMessages();

    // Subscribe to real-time message updates
    const subscription = flooringBotDb.subscribeToMessages(conversation.id, (newMessage) => {
      setMessages((prev) => [...prev, {
        id: newMessage.id,
        conversationId: newMessage.conversation_id,
        participantId: newMessage.sender_type === 'client' ? 'user' : 'ai-1',
        content: newMessage.content,
        timestamp: new Date(newMessage.created_at),
        isRead: true
      }]);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [conversation?.id]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const loadMessages = async () => {
    if (!conversation?.id) return;
    setLoading(true);
    const data = await flooringBotDb.getMessages(conversation.id);
    setMessages(data);
    setLoading(false);
  };

  const handleSend = async () => {
    if (!inputValue.trim() || sending || !conversation?.id) return;

    setSending(true);
    try {
      await flooringBotDb.sendMessage(conversation.id, inputValue.trim(), 'client');
      setInputValue('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleAutoResponse = async () => {
    const newValue = !autoResponseEnabled;
    setAutoResponseEnabled(newValue);

    // Update in database
    try {
      await flooringBotDb.supabase
        .from('conversations')
        .update({ metadata: { auto_response_enabled: newValue } })
        .eq('id', conversation.id);
    } catch (error) {
      console.error('Failed to toggle auto-response:', error);
    }
  };

  const generateAIResponse = async () => {
    if (generating || !conversation?.id) return;

    setGenerating(true);
    try {
      // Call AI handler to generate response
      const response = await fetch('/api/ai/generate-response', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: conversation.id })
      });

      const data = await response.json();
      if (data.response) {
        setInputValue(data.response);
      }
    } catch (error) {
      console.error('Failed to generate AI response:', error);
    } finally {
      setGenerating(false);
    }
  };

  const formatTime = (timestamp: string | number | Date): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleReply = (message: Message) => {
    setReplyingTo(message);
    setInputValue(`\n\n---\nOn ${new Date(message.timestamp).toLocaleString()}, ${messages.find(m => m.id === message.id)?.participantId} wrote:\n> ${message.content}\n`);
  };

  const handleAttachmentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments(Array.from(e.target.files));
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col h-full bg-[#0d1117]">
      {/* Header */}
      <div className="border-b border-[#30363d] bg-[#161b22] shrink-0">
        <div className="flex items-center justify-between p-4">
          <h2 className="text-xl font-semibold text-gray-200">
            {conversation.title}
          </h2>

          {/* Controls */}
          <div className="flex items-center gap-4">
            {/* Auto-response toggle */}
            <button
              onClick={toggleAutoResponse}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${
                autoResponseEnabled
                  ? 'bg-green-500/20 border-green-500/50 text-green-400'
                  : 'bg-gray-500/20 border-gray-500/50 text-gray-400'
              }`}
              title="Toggle automatic AI responses"
            >
              <Power className="w-4 h-4" />
              <span className="text-xs font-medium">
                Auto AI {autoResponseEnabled ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* Participant Avatars */}
            <div className="flex items-center gap-2">
              {conversation.participants?.map((participant, index) => (
                <div
                  key={index}
                  className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600
                           flex items-center justify-center text-sm font-semibold text-white
                           cursor-pointer hover:scale-110 transition-transform shadow-lg"
                  title={participant.name}
                >
                  {participant.avatar || participant.name.charAt(0).toUpperCase()}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Message List */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 custom-scrollbar min-h-0"
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
            <div className="text-gray-500 text-sm mt-3">Loading messages...</div>
          </div>
        ) : (
          <>
            {messages.map((message: Message, index: number) => {
              const participant = conversation.participants.find(p => p.id === message.participantId);

              // Debug logging
              if (index === 0) {
                console.log('First message:', {
                  participantId: message.participantId,
                  participant,
                  allParticipants: conversation.participants
                });
              }

              const isClient = participant?.role === 'user';
              const isBot = participant?.role === 'ai';
              const isUs = participant?.role === 'support';

              return (
                <div
                  key={index}
                  className={`flex items-start gap-3 ${isClient ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  {/* Avatar */}
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center shrink-0
                    ${isClient
                      ? 'bg-gradient-to-br from-gray-600 to-gray-700'
                      : isBot
                        ? 'bg-gradient-to-br from-purple-500 to-pink-600'
                        : 'bg-gradient-to-br from-cyan-500 to-blue-600'
                    }
                  `}>
                    {isClient ? (
                      <User className="w-4 h-4 text-white" />
                    ) : isBot ? (
                      <Bot className="w-4 h-4 text-white" />
                    ) : (
                      <User className="w-4 h-4 text-white" />
                    )}
                  </div>

                  {/* Message Bubble */}
                  <div className="flex flex-col max-w-[70%]">
                    <div
                      className={`
                        group relative rounded-2xl px-4 py-3 shadow-md
                        ${isClient
                          ? 'bg-[#1c2128] text-gray-200 border border-[#30363d] rounded-tl-sm'
                          : isBot
                            ? 'bg-gradient-to-br from-purple-600/20 to-pink-600/20 text-gray-200 border border-purple-500/30 rounded-tr-sm'
                            : 'bg-gradient-to-br from-cyan-600 to-blue-700 text-white rounded-tr-sm'
                        }
                      `}
                    >
                      {/* Sender Name */}
                      <div className={`
                        text-xs font-semibold mb-1
                        ${isClient ? 'text-gray-400' : isBot ? 'text-purple-300' : 'text-cyan-100'}
                      `}>
                        {participant?.name || (isClient ? 'Client' : isBot ? 'AI Assistant' : 'You')}
                      </div>

                      {/* Message Content */}
                      <div
                        className="text-sm leading-relaxed break-words prose prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: message.content }}
                      />

                      {/* Timestamp & Actions */}
                      <div className="flex items-center justify-between mt-2 gap-2">
                        <div className={`
                          text-xs
                          ${isClient ? 'text-gray-500' : isBot ? 'text-purple-300/70' : 'text-cyan-200/70'}
                        `}>
                          {formatTime(message.timestamp)}
                        </div>

                        {/* Email Actions */}
                        {isEmailConversation && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleReply(message)}
                              className="p-1 hover:bg-white/10 rounded transition-colors"
                              title="Reply"
                            >
                              <Reply className="w-3 h-3" />
                            </button>
                            {message.metadata?.attachments && message.metadata.attachments.length > 0 && (
                              <button
                                className="p-1 hover:bg-white/10 rounded transition-colors"
                                title="View attachments"
                              >
                                <Download className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Attachments */}
                    {message.metadata?.attachments && message.metadata.attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {message.metadata.attachments.map((att: any, i: number) => (
                          <div key={i} className="flex items-center gap-2 p-2 bg-white/5 rounded text-xs">
                            <Paperclip className="w-3 h-3" />
                            <span className="flex-1 truncate">{att.filename}</span>
                            <Download className="w-3 h-3 cursor-pointer hover:text-cyan-400" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Input Box */}
      <div className="border-t border-[#30363d] bg-[#161b22] p-4 shrink-0">
        {/* Reply Banner */}
        {replyingTo && (
          <div className="mb-3 p-2 bg-[#1c2128] border border-[#30363d] rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Reply className="w-3 h-3" />
              <span>Replying to message</span>
            </div>
            <button
              onClick={() => {
                setReplyingTo(null);
                setInputValue('');
              }}
              className="text-gray-500 hover:text-gray-300"
            >
              ×
            </button>
          </div>
        )}

        {/* Attachments Preview */}
        {attachments.length > 0 && (
          <div className="mb-3 space-y-1">
            {attachments.map((file, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-[#1c2128] border border-[#30363d] rounded-lg">
                <Paperclip className="w-4 h-4 text-gray-400" />
                <span className="flex-1 text-xs text-gray-300 truncate">{file.name}</span>
                <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)}KB</span>
                <button
                  onClick={() => removeAttachment(i)}
                  className="text-gray-500 hover:text-red-400 transition-colors"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          {/* Attachment Button for Email */}
          {isEmailConversation && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleAttachmentSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-3 hover:bg-[#1c2128] rounded-lg transition-colors"
                title="Attach files"
              >
                <Paperclip className="w-5 h-5 text-gray-400" />
              </button>
            </>
          )}

          <textarea
            placeholder={isEmailConversation ? "Compose email..." : "Type your message..."}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending}
            rows={3}
            className="flex-1 px-4 py-3 bg-[#0d1117] border border-[#30363d] rounded-lg
                     text-gray-300 placeholder-gray-500 text-sm resize-none
                     focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500
                     disabled:opacity-50 disabled:cursor-not-allowed
                     transition-all"
          />
          {/* Generate AI Response button */}
          <button
            onClick={generateAIResponse}
            disabled={generating}
            className="px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600
                     hover:from-purple-500 hover:to-pink-500
                     disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed
                     text-white font-medium rounded-lg
                     flex items-center gap-2 transition-all
                     shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40
                     disabled:shadow-none h-fit"
            title="Generate AI response"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                <span>Generate</span>
              </>
            )}
          </button>

          <button
            onClick={handleSend}
            disabled={sending || !inputValue.trim()}
            className="px-5 py-3 bg-gradient-to-r from-cyan-600 to-blue-700
                     hover:from-cyan-500 hover:to-blue-600
                     disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed
                     text-white font-medium rounded-lg
                     flex items-center gap-2 transition-all
                     shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40
                     disabled:shadow-none h-fit"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                {isEmailConversation ? <Mail className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                <span>Send</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
